import type { PointerEvent, ReactNode } from "react";

export type PtzButtonProps = {
  className?: string;
  ariaLabel: string;
  title?: string;
  count: number;
  full: boolean;
  disabled?: boolean;
  ariaSuffix?: string;
  /** @deprecated Use onPressStart/onPressEnd for continuous PTZ. */
  onPress?: () => void;
  onPressStart?: () => void;
  onPressEnd?: () => void;
  children: ReactNode;
};

export function PtzButton({
  className,
  ariaLabel,
  title,
  count,
  full,
  disabled = false,
  ariaSuffix,
  onPress,
  onPressStart,
  onPressEnd,
  children,
}: PtzButtonProps) {
  const reasonSuffix = disabled
    ? "at end of travel"
    : full
      ? "buffer full"
      : ariaSuffix;
  const composedLabel = reasonSuffix ? `${ariaLabel} — ${reasonSuffix}` : ariaLabel;
  const active = count > 0;
  const blocked = full || disabled;
  const composedClass = [
    "ds-ptz-btn",
    className ?? "",
    active ? "ds-ptz-btn--active" : "",
    full ? "ds-ptz-btn--full" : "",
    disabled ? "ds-ptz-btn--limit" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const handlePointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (blocked || e.button !== 0) return;
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    onPressStart?.();
  };

  const release = (e: PointerEvent<HTMLButtonElement>) => {
    if (blocked) return;
    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch {
      /* ignore */
    }
    onPressEnd?.();
  };

  const handleClick = () => {
    if (blocked) return;
    if (onPressStart && onPressEnd) return;
    onPress?.();
  };

  return (
    <button
      type="button"
      className={composedClass}
      aria-label={composedLabel}
      aria-disabled={blocked || undefined}
      title={title ?? ariaLabel}
      onPointerDown={handlePointerDown}
      onPointerUp={release}
      onPointerCancel={release}
      onLostPointerCapture={release}
      onClick={handleClick}
    >
      <span className="ds-ptz-btn__label">{children}</span>
      {active ? (
        <span
          className="ds-ptz-btn__count"
          aria-hidden
          data-count={count > 99 ? 99 : count}
        >
          {count > 99 ? 99 : count}
        </span>
      ) : null}
      {active ? (
        <span className="ds-ptz-btn__progress" aria-hidden>
          <span className="ds-ptz-btn__progress-bar" />
        </span>
      ) : null}
    </button>
  );
}
